import { PRODUCTION_GROUP_TAG } from '@kontor/shared'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  Bell,
  FileText,
  FolderOpen,
  Globe,
  ListChecks,
  HardDriveDownload,
  Keyboard,
  LayoutDashboard,
  Lock,
  Rocket,
  Server as ServerIcon,
  Settings,
  ShieldCheck,
  SunMoon,
  TerminalSquare,
  Terminal,
  type LucideIcon,
} from 'lucide-react'
import { useTheme } from 'next-themes'
import { useMemo, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { fetchServers } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Command {
  id: string
  label: string
  /** Second line and extra search text: server host, section name, anything typed to find it. */
  hint?: string
  group: string
  icon: LucideIcon
  production?: boolean
  run: () => void
}

/** The per-server pages, in the order the navigation shows them. */
const SERVER_PAGES = [
  { segment: 'prozesse', label: 'Prozesse', icon: Activity },
  { segment: 'dateien', label: 'Dateien', icon: FolderOpen },
  { segment: 'terminal', label: 'Terminal', icon: TerminalSquare },
  { segment: 'ssl', label: 'SSL', icon: ShieldCheck },
  { segment: 'erreichbar', label: 'Erreichbar', icon: Globe },
  { segment: 'checkliste', label: 'Checkliste', icon: ListChecks },
  { segment: 'sicherheit', label: 'Sicherheit', icon: ShieldCheck },
] as const

const PAGES = [
  { path: '/', label: 'Übersicht', icon: LayoutDashboard },
  { path: '/server', label: 'Server', icon: ServerIcon },
  { path: '/projekte', label: 'Projekte', icon: Rocket },
  { path: '/backups', label: 'Backups', icon: HardDriveDownload },
  { path: '/berichte', label: 'Berichte', icon: FileText },
  { path: '/alarme', label: 'Alarme', icon: Bell },
  { path: '/snippets', label: 'Snippets', icon: Terminal },
  { path: '/einstellungen', label: 'Einstellungen', icon: Settings },
] as const

/**
 * Matches every word of the query separately, so "kunde term" finds the terminal of kunde-vps.
 * Deliberately not fuzzy: with a handful of servers, fuzzy matching mostly produces surprises.
 */
function matches(command: Command, query: string): boolean {
  const haystack = `${command.label} ${command.hint ?? ''} ${command.group}`.toLowerCase()
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word !== '')
    .every((word) => haystack.includes(word))
}

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onLock: () => void
  onShowHelp: () => void
}

/** Ctrl+K: every server, every page and the few global actions in one list. */
export function CommandPalette({ open, onOpenChange, onLock, onShowHelp }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { resolvedTheme, setTheme } = useTheme()
  const [query, setQuery] = useState('')
  const [activeId, setActiveId] = useState<string | null>(null)

  // Only fetched while the palette is open; it is not worth a request otherwise.
  const servers = useQuery({ queryKey: ['servers'], queryFn: fetchServers, enabled: open })

  const commands = useMemo<Command[]>(() => {
    const close = (action: () => void) => () => {
      onOpenChange(false)
      action()
    }

    const list: Command[] = PAGES.map((page) => ({
      id: `page:${page.path}`,
      label: page.label,
      group: 'Seiten',
      icon: page.icon,
      run: close(() => void navigate(page.path)),
    }))

    for (const server of servers.data ?? []) {
      for (const page of SERVER_PAGES) {
        list.push({
          id: `server:${server.id}:${page.segment}`,
          label: `${server.name} · ${page.label}`,
          hint: `${server.username}@${server.host}`,
          group: 'Server',
          icon: page.icon,
          production: server.groupTag === PRODUCTION_GROUP_TAG,
          run: close(() => void navigate(`/server/${server.id}/${page.segment}`)),
        })
      }
    }

    list.push(
      {
        id: 'action:lock',
        label: 'Vault sperren',
        hint: 'Strg+Umschalt+L',
        group: 'Aktionen',
        icon: Lock,
        run: close(onLock),
      },
      {
        id: 'action:theme',
        label: resolvedTheme === 'dark' ? 'Zu hell wechseln' : 'Zu dunkel wechseln',
        hint: 'Erscheinungsbild',
        group: 'Aktionen',
        icon: SunMoon,
        run: close(() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')),
      },
      {
        id: 'action:help',
        label: 'Tastaturkürzel anzeigen',
        hint: '?',
        group: 'Aktionen',
        icon: Keyboard,
        run: close(onShowHelp),
      },
    )

    return list
  }, [navigate, onLock, onOpenChange, onShowHelp, resolvedTheme, servers.data, setTheme])

  const visible = useMemo(
    () => (query.trim() === '' ? commands : commands.filter((command) => matches(command, query))),
    [commands, query],
  )

  // Derived rather than stored: when the filter changes the remembered id may be gone, and the
  // first entry is then the right selection. Syncing that in an effect would flicker.
  const active = visible.find((command) => command.id === activeId) ?? visible[0]

  function move(delta: number): void {
    if (visible.length === 0) return
    const index = visible.findIndex((command) => command.id === active?.id)
    const next = visible[(index + delta + visible.length) % visible.length]
    if (next) setActiveId(next.id)
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      move(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      move(-1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      active?.run()
    }
  }

  let lastGroup = ''

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setQuery('')
          setActiveId(null)
        }
        onOpenChange(next)
      }}
    >
      {/* No close button: it would sit on top of the search field, and Esc is the way out of a
          palette anyway. */}
      <DialogContent
        showCloseButton={false}
        className="top-[20%] max-w-xl translate-y-0 gap-0 overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">Befehle</DialogTitle>
        <DialogDescription className="sr-only">
          Suche nach Servern, Seiten und Aktionen. Mit den Pfeiltasten wählen, mit Enter öffnen.
        </DialogDescription>

        <Input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Server, Seite oder Aktion …"
          aria-label="Suche"
          aria-controls="command-list"
          className="h-12 rounded-none border-0 border-b px-4 text-base focus-visible:ring-0"
        />

        <div id="command-list" role="listbox" className="max-h-80 overflow-y-auto p-1">
          {visible.length === 0 ? (
            <p className="text-muted-foreground px-3 py-6 text-center text-sm">
              Nichts gefunden. Andere Schreibweise?
            </p>
          ) : (
            visible.map((command) => {
              const heading = command.group === lastGroup ? null : command.group
              lastGroup = command.group
              const isActive = command.id === active?.id

              return (
                <div key={command.id}>
                  {heading ? (
                    <p className="text-muted-foreground px-3 pt-3 pb-1 text-xs font-medium">
                      {heading}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onMouseMove={() => setActiveId(command.id)}
                    onClick={command.run}
                    ref={(node) => {
                      if (isActive) node?.scrollIntoView({ block: 'nearest' })
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm',
                      isActive ? 'bg-accent text-accent-foreground' : 'text-foreground',
                    )}
                  >
                    <command.icon className="size-4 shrink-0 opacity-70" />
                    <span className="truncate">{command.label}</span>
                    {command.production ? (
                      <Badge variant="destructive" className="shrink-0">
                        {PRODUCTION_GROUP_TAG}
                      </Badge>
                    ) : null}
                    {command.hint ? (
                      <span className="text-muted-foreground ml-auto shrink-0 truncate font-mono text-xs">
                        {command.hint}
                      </span>
                    ) : null}
                  </button>
                </div>
              )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
